import React, { useEffect, useState } from 'react';
import { Table, Select, Button, Space, message, Switch, Card, Row, Col, Typography, Tabs } from 'antd';
import {
    UserOutlined, PlusOutlined, DeleteOutlined,
    CheckOutlined, CloseOutlined, LockOutlined
} from '@ant-design/icons';
import axiosInstance from '../../../axiosInstance';
import { getUserData } from '../../constants';

const { Option } = Select;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const userData = getUserData();
const rolePermission = userData && userData?.user ? userData?.user?.role?.role_permission : {};

const RolePermissionTable = ({ defaultTab = '1' }) => {
    // ---------------- ROLES STATE ----------------
    const [roles, setRoles] = useState([]);
    const [allRoles, setAllRoles] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(true);

    // ---------------- EMPLOYEES STATE ----------------
    const [userPermissions, setUserPermissions] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);

    const [activeTab, setActiveTab] = useState(defaultTab);

    // ---------------- FETCH DATA ----------------
    useEffect(() => {
        const fetchRolesData = async () => {
            try {
                const response = await axiosInstance.get('/role-permission');
                const filteredRoles = (response.data || []).filter(role => {
                    return role.role_id !== 3 || rolePermission.is_system_owner === 1;
                });
                setRoles(filteredRoles);
            } catch (error) {
                message.error('Error fetching roles and permissions.');
                console.error(error);
            } finally {
                setLoadingRoles(false);
            }
        };

        const fetchAllRoles = async () => {
            try {
                const response = await axiosInstance.get('/all-roles');
                const filteredRoles = response.data.roles.filter(
                    (role) => role.id !== 3 || rolePermission.is_system_owner === 1
                );
                setAllRoles(filteredRoles);
            } catch (error) {
                message.error('Error fetching all roles.');
                console.error(error);
            }
        };

        const fetchUserPermissions = async () => {
            try {
                const response = await axiosInstance.get('/user-permission');
                setUserPermissions(response.data || []);
            } catch (error) {
                message.error('Error fetching user permissions.');
                console.error(error);
            } finally {
                setLoadingUsers(false);
            }
        };

        const fetchAllUsers = async () => {
            try {
                const response = await axiosInstance.get('/employees');
                const users = response.data.employees.map(emp => emp.user);
                setAllUsers(users);
            } catch (error) {
                message.error('Error fetching all users.');
                console.error(error);
            }
        };

        fetchRolesData();
        fetchAllRoles();
        fetchUserPermissions();
        fetchAllUsers();
    }, [roles.length]);

    // ---------------- ROLES OPERATIONS ----------------
    const saveRoleChange = async (updatedRole) => {
        const permissions = {
            is_stock: updatedRole.is_stock,
            is_sales: updatedRole.is_sales,
            is_manage: updatedRole.is_manage,
        };

        const roleWithPermissions = {
            role_id: updatedRole.role_id,
            permissions: permissions,
        };

        try {
            let response;
            if (updatedRole.id) {
                response = await axiosInstance.put(`/role-permission/${updatedRole.id}`, roleWithPermissions);
                message.success('Role updated successfully.');
            } else {
                response = await axiosInstance.post('/role-permission', roleWithPermissions);
                message.success('Role created successfully.');
            }
            window.location.reload();
        } catch (error) {
            message.error('Error saving role.');
            console.error(error);
        }
    };

    const handleRoleChange = (index, field, value) => {
        const updatedRole = {
            ...roles[index],
            [field]: value,
        };

        setRoles((prevRoles) => {
            const updatedRoles = [...prevRoles];
            updatedRoles[index] = updatedRole;
            return updatedRoles;
        });

        saveRoleChange(updatedRole);
    };

    const addRoleRow = () => {
        setRoles((prevRoles) => [
            ...prevRoles,
            { id: null, role_id: '', is_sales: false, is_manage: false, is_stock: false },
        ]);
    };

    const removeRoleRow = async (index) => {
        const roleToRemove = roles[index];
        if (roleToRemove.id) {
            try {
                await axiosInstance.delete(`/role-permission/${roleToRemove.id}`);
                message.success('Role removed successfully.');
            } catch (error) {
                message.error('Error removing role.');
                console.error(error);
            }
        }
        setRoles((prevRoles) => prevRoles.filter((_, i) => i !== index));
    };

    // ---------------- USER PERMISSIONS OPERATIONS ----------------
    const saveUserPermissionChange = async (updatedPermission) => {
        const permissionData = {
            user_id: updatedPermission.user_id,
            is_approve: updatedPermission.is_approve,
            is_positive_adjustment: updatedPermission.is_positive_adjustment,
            is_negative_adjustment: updatedPermission.is_negative_adjustment,
            is_wastage: updatedPermission.is_wastage,
            is_reverse: updatedPermission.is_reverse,
            is_transfer: updatedPermission.is_transfer,
            is_register_product: updatedPermission.is_register_product,
            is_view_profit: updatedPermission.is_view_profit
        };

        try {
            let response;
            if (updatedPermission.id) {
                response = await axiosInstance.put(`/user-permission/${updatedPermission.id}`, permissionData);
            } else {
                response = await axiosInstance.post('/user-permission', permissionData);
            }
            const updatedData = response.data;
            setUserPermissions((prevPermissions) =>
                prevPermissions.map((permission) =>
                    permission.id === updatedPermission.id
                        ? { ...permission, id: updatedData.id }
                        : permission
                )
            );
            message.success(updatedPermission.id ? 'User permission updated successfully.' : 'User permission created successfully.');
        } catch (error) {
            message.error('Error saving user permission.');
            console.error(error);
        }
    };

    const handleUserPermissionChange = (index, field, value) => {
        const updatedPermission = {
            ...userPermissions[index],
            [field]: value,
        };

        setUserPermissions((prevPermissions) => {
            const updatedPermissions = [...prevPermissions];
            updatedPermissions[index] = updatedPermission;
            return updatedPermissions;
        });

        saveUserPermissionChange(updatedPermission);
    };

    const addPermissionRow = () => {
        setUserPermissions((prevPermissions) => [
            ...prevPermissions,
            {
                id: null,
                user_id: '',
                is_approve: false,
                is_positive_adjustment: false,
                is_negative_adjustment: false,
                is_wastage: false,
                is_reverse: false,
                is_transfer: false,
                is_register_product: false,
                is_view_profit: false
            },
        ]);
    };

    const removePermissionRow = async (index) => {
        const permissionToRemove = userPermissions[index];
        if (permissionToRemove.id) {
            try {
                await axiosInstance.delete(`/user-permission/${permissionToRemove.id}`);
                message.success('User permission removed successfully.');
            } catch (error) {
                message.error('Error removing user permission.');
                console.error(error);
            }
        }
        setUserPermissions((prevPermissions) => prevPermissions.filter((_, i) => i !== index));
    };

    // ---------------- TABLE COLUMNS DEFINITION ----------------
    const roleColumns = [
        {
            title: 'Role',
            dataIndex: 'role_id',
            render: (roleId, record, index) => {
                const isEditable = ![1, 2, 3].includes(roleId);

                const selectableRoles = allRoles.filter(
                    (role) => role.id === roleId || !roles.some((r) => r.role_id === role.id)
                );

                return (
                    <Select
                        showSearch
                        value={roleId || undefined}
                        onChange={(value) => handleRoleChange(index, 'role_id', value)}
                        style={{ width: '100%', minWidth: '150px' }}
                        placeholder="Select Role"
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            option.children.toLowerCase().includes(input.toLowerCase())
                        }
                        disabled={!isEditable}
                    >
                        {selectableRoles.map((role) => (
                            <Option key={role.id} value={role.id}>
                                {role.name}
                            </Option>
                        ))}
                    </Select>
                );
            },
        },
        {
            title: 'Is Sales',
            dataIndex: 'is_sales',
            align: 'center',
            render: (_, record, index) => {
                const isEditable = ![1, 2, 3].includes(record.role_id);
                return (
                    <Switch
                        checked={record.is_sales}
                        onChange={(checked) => handleRoleChange(index, 'is_sales', checked)}
                        disabled={!isEditable}
                        checkedChildren={<CheckOutlined />}
                        unCheckedChildren={<CloseOutlined />}
                    />
                );
            },
        },
        {
            title: 'Is Manage',
            dataIndex: 'is_manage',
            align: 'center',
            render: (_, record, index) => {
                return (
                    <Switch
                        checked={record.is_manage}
                        onChange={(checked) => handleRoleChange(index, 'is_manage', checked)}
                        disabled={true}
                        checkedChildren={<CheckOutlined />}
                        unCheckedChildren={<CloseOutlined />}
                    />
                );
            },
        },
        ...(rolePermission.is_system_owner === 1
            ? [{
                title: 'Is System Owner',
                dataIndex: 'is_system_owner',
                align: 'center',
                render: (_, record, index) => {
                    return (
                        <Switch
                            checked={record.is_system_owner}
                            onChange={(checked) => handleRoleChange(index, 'is_system_owner', checked)}
                            disabled={true}
                            checkedChildren={<CheckOutlined />}
                            unCheckedChildren={<CloseOutlined />}
                        />
                    );
                },
            }]
            : []),
        {
            title: 'Is Stock',
            dataIndex: 'is_stock',
            align: 'center',
            render: (_, record, index) => {
                const isEditable = ![1, 2, 3].includes(record.role_id);
                return (
                    <Switch
                        checked={record.is_stock}
                        onChange={(checked) => handleRoleChange(index, 'is_stock', checked)}
                        disabled={!isEditable}
                        checkedChildren={<CheckOutlined />}
                        unCheckedChildren={<CloseOutlined />}
                    />
                );
            },
        },
        {
            title: 'Action',
            align: 'center',
            render: (_, __, index) => {
                const isEditable = ![1, 2, 3].includes(roles[index].role_id);
                return (
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeRoleRow(index)}
                        disabled={!isEditable}
                    >
                        Remove
                    </Button>
                );
            },
        },
    ];

    const employeeColumns = [
        {
            title: 'User',
            dataIndex: 'user_id',
            render: (userId, record, index) => {
                const selectedUser = allUsers.find((user) => user.id === userId);
                return (
                    <Select
                        showSearch
                        value={selectedUser ? selectedUser.name : ''}
                        onChange={(value) => handleUserPermissionChange(index, 'user_id', value)}
                        style={{ width: '100%', minWidth: '180px' }}
                        placeholder="Select User"
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                            const optionChildren = option?.children?.toLowerCase() || '';
                            return optionChildren.includes(input.toLowerCase());
                        }}
                    >
                        {allUsers
                            .filter((user) => !userPermissions.some((r) => r.user_id === user.id))
                            .map((user) => (
                                <Option key={user.id} value={user.id}>
                                    {user.name}
                                </Option>
                            ))}
                    </Select>
                );
            },
        },
        {
            title: 'Is Approved',
            dataIndex: 'is_approve',
            align: 'center',
            render: (_, record, index) => (
                <Switch
                    checked={!!record.is_approve}
                    onChange={(checked) => handleUserPermissionChange(index, 'is_approve', checked)}
                    size="small"
                />
            ),
        },
        {
            title: 'Positive Adj',
            dataIndex: 'is_positive_adjustment',
            align: 'center',
            render: (_, record, index) => (
                <Switch
                    checked={!!record.is_positive_adjustment}
                    onChange={(checked) => handleUserPermissionChange(index, 'is_positive_adjustment', checked)}
                    size="small"
                />
            ),
        },
        {
            title: 'Negative Adj',
            dataIndex: 'is_negative_adjustment',
            align: 'center',
            render: (_, record, index) => (
                <Switch
                    checked={!!record.is_negative_adjustment}
                    onChange={(checked) => handleUserPermissionChange(index, 'is_negative_adjustment', checked)}
                    size="small"
                />
            ),
        },
        {
            title: 'Wastage Adj',
            dataIndex: 'is_wastage',
            align: 'center',
            render: (_, record, index) => (
                <Switch
                    checked={!!record.is_wastage}
                    onChange={(checked) => handleUserPermissionChange(index, 'is_wastage', checked)}
                    size="small"
                />
            ),
        },
        {
            title: 'Reverse Paid',
            dataIndex: 'is_reverse',
            align: 'center',
            render: (_, record, index) => (
                <Switch
                    checked={!!record.is_reverse}
                    onChange={(checked) => handleUserPermissionChange(index, 'is_reverse', checked)}
                    size="small"
                />
            ),
        },
        {
            title: 'Is Transfer',
            dataIndex: 'is_transfer',
            align: 'center',
            render: (_, record, index) => (
                <Switch
                    checked={!!record.is_transfer}
                    onChange={(checked) => handleUserPermissionChange(index, 'is_transfer', checked)}
                    size="small"
                />
            ),
        },
        {
            title: 'Register Product',
            dataIndex: 'is_register_product',
            align: 'center',
            render: (_, record, index) => (
                <Switch
                    checked={!!record.is_register_product}
                    onChange={(checked) => handleUserPermissionChange(index, 'is_register_product', checked)}
                    size="small"
                />
            ),
        },
        {
            title: 'View Profit',
            dataIndex: 'is_view_profit',
            align: 'center',
            render: (_, record, index) => (
                <Switch
                    checked={!!record.is_view_profit}
                    onChange={(checked) => handleUserPermissionChange(index, 'is_view_profit', checked)}
                    size="small"
                />
            ),
        },
        {
            title: 'Action',
            align: 'center',
            render: (_, __, index) => (
                <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removePermissionRow(index)}
                >
                    Remove
                </Button>
            ),
        },
    ];

    return (
        <div style={{ padding: '4px' }}>
            {/* Header Banner */}
            <div className="welcome-banner-card" style={{ marginBottom: '24px' }}>
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col xs={24} md={18}>
                        <Title level={2} className="welcome-title" style={{ margin: 0, fontWeight: '800', letterSpacing: '-0.5px' }}>
                            Security & Permissions Hub
                        </Title>
                        <Text className="welcome-subtitle" style={{ fontSize: '15px', display: 'block', marginTop: '6px' }}>
                            Configure authorization levels across different organizational roles and set individual overrides for employees.
                        </Text>
                    </Col>
                    <Col xs={24} md={6} className="welcome-time-col">
                        <div className="welcome-time-container">
                            <LockOutlined style={{ marginRight: '8px' }} />
                            <span>System Owner Panel</span>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Quick Stats Grid */}
            <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12}>
                    <Card className="stat-card border-top-products" bordered={false} bodyStyle={{ padding: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <span className="stat-card-title">Configured Group Roles</span>
                                <Title level={3} className="stat-card-value" style={{ margin: '4px 0 0 0' }}>
                                    {roles.length}
                                </Title>
                            </div>
                            <div className="stat-card-icon-container bg-products">
                                <LockOutlined className="stat-card-icon color-products" />
                            </div>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12}>
                    <Card className="stat-card border-top-invoices" bordered={false} bodyStyle={{ padding: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <span className="stat-card-title">Employee Overrides</span>
                                <Title level={3} className="stat-card-value" style={{ margin: '4px 0 0 0' }}>
                                    {userPermissions.length}
                                </Title>
                            </div>
                            <div className="stat-card-icon-container bg-invoices">
                                <UserOutlined className="stat-card-icon color-invoices" />
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Main Tabs Container */}
            <Card className="dashboard-card" bordered={false} bodyStyle={{ padding: '24px' }}>
                <Tabs activeKey={activeTab} onChange={setActiveTab} size="large" centered>
                    <TabPane
                        tab={
                            <span>
                                <LockOutlined style={{ marginRight: 8 }} />
                                Role-Based Permissions
                            </span>
                        }
                        key="1"
                    >
                        <div style={{ marginTop: '16px' }}>
                            <Table
                                dataSource={roles}
                                columns={roleColumns}
                                pagination={false}
                                rowKey={(record) => record.id || `new-${record.role_id}`}
                                style={{ marginBottom: '20px' }}
                                scroll={{ x: true }}
                                loading={loadingRoles}
                            />
                            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                <Button
                                    type="dashed"
                                    onClick={addRoleRow}
                                    icon={<PlusOutlined />}
                                    size="large"
                                    style={{ width: '200px', borderRadius: '8px' }}
                                >
                                    Add Role
                                </Button>
                            </div>
                        </div>
                    </TabPane>

                    <TabPane
                        tab={
                            <span>
                                <UserOutlined style={{ marginRight: 8 }} />
                                Employee Overrides
                            </span>
                        }
                        key="2"
                    >
                        <div style={{ marginTop: '16px' }}>
                            <Table
                                dataSource={userPermissions}
                                columns={employeeColumns}
                                pagination={false}
                                rowKey={(record) => record.id || `new-${record.user_id}`}
                                style={{ marginBottom: '20px' }}
                                scroll={{ x: true }}
                                loading={loadingUsers}
                            />
                            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                <Button
                                    type="dashed"
                                    onClick={addPermissionRow}
                                    icon={<PlusOutlined />}
                                    size="large"
                                    style={{ width: '200px', borderRadius: '8px' }}
                                >
                                    Add Override
                                </Button>
                            </div>
                        </div>
                    </TabPane>
                </Tabs>
            </Card>
        </div>
    );
};

export default RolePermissionTable;
